import { useState, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Unit } from '@/shared/model';

interface UnitsTableProps {
    units: Unit[];
    onUpdate: (id: string, name: string) => void;
    onDelete: (id: string) => void;
}

export const UnitsTable: FC<UnitsTableProps> = ({ 
    units, 
    onUpdate, 
    onDelete 
}) => {
    const { t } = useTranslation();
    const [exitingIds, setExitingIds] = useState<string[]>([]);

    const handleRemoveWithAnimation = (id: string) => {
        setExitingIds(prev => [...prev, id]);
        setTimeout(() => {
            onDelete(id);
            setExitingIds(prev => prev.filter(eid => eid !== id));
        }, 300);
    };

    return (
        <table className="w-full text-sm text-left">
            <thead className="text-[10px] text-slate-500 dark:text-slate-400 uppercase bg-blue-50/60 dark:bg-slate-800/80 backdrop-blur-sm border-b border-blue-100 dark:border-slate-800 sticky top-0 z-20 shadow-sm">
                <tr>
                    <th className="px-6 py-4 font-semibold tracking-wider">{t('catalog.table.name', 'Название')}</th>
                    <th className="hidden md:table-cell px-4 py-4 font-semibold tracking-wider">{t('catalog.table.type', 'Тип')}</th>
                    <th className="hidden md:table-cell w-16"></th>
                </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                {units.map(u => {
                    const isExiting = exitingIds.includes(u.id);
                    return (
                        <tr 
                            key={u.id} 
                            className={`
                                transition-colors duration-200 ease-in-out
                                ${isExiting ? 'bg-red-50 dark:bg-red-900/10 pointer-events-none' : 'hover:bg-blue-50/30 dark:hover:bg-blue-900/10'}
                            `}
                        >
                            <td className={`px-6 transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${isExiting ? '!py-0 !border-0' : 'py-4'}`}>
                                <div className={`transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] overflow-hidden ${isExiting ? 'max-h-0 opacity-0' : 'max-h-20 opacity-100'}`}>
                                    <div className="flex items-center justify-between gap-2">
                                        <input 
                                            value={u.name}
                                            onChange={(e) => onUpdate(u.id, e.target.value)}
                                            className="w-full bg-transparent border-b border-transparent hover:border-slate-300 dark:hover:border-slate-600 focus:border-blue-400 dark:focus:border-blue-500 focus:outline-none transition-colors duration-150 font-bold text-slate-700 dark:text-slate-200 py-1"
                                        />
                                        <div className="md:hidden flex items-center gap-2">
                                            <span className={`text-[10px] font-bold px-2 py-1 rounded-md h-7 flex items-center ${u.type === 'system' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'}`}>
                                                {u.type === 'system'
                                                    ? t('catalog.unit_types.system_short', 'SYS')
                                                    : t('catalog.unit_types.user_short', 'USR')}
                                            </span>
                                            {u.type !== 'system' && (
                                                <button
                                                    onClick={() => handleRemoveWithAnimation(u.id)}
                                                    aria-label={t('catalog.delete_unit', 'Удалить единицу')}
                                                    className="w-8 h-8 flex items-center justify-center text-slate-300 dark:text-slate-600 hover:text-red-500 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                                >
                                                    <Trash2 size={18} aria-hidden="true" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td className={`hidden md:table-cell px-4 transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${isExiting ? '!py-0 !border-0' : 'py-4'}`}>
                                <div className={`transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] overflow-hidden ${isExiting ? 'max-h-0 opacity-0' : 'max-h-20 opacity-100'}`}>
                                    <span className={`text-[10px] font-bold px-2 py-1 rounded-md ${u.type === 'system' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'}`}>
                                        {u.type === 'system' ? t('catalog.unit_types.system', 'СИСТЕМНАЯ') : t('catalog.unit_types.user', 'ПОЛЬЗОВАТЕЛЬСКАЯ')}
                                    </span>
                                </div>
                            </td>
                            <td className={`hidden md:table-cell px-4 text-right transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${isExiting ? '!py-0 !border-0' : 'py-4'}`}>
                                <div className={`transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] overflow-hidden ${isExiting ? 'max-h-0 opacity-0' : 'max-h-20 opacity-100'}`}>
                                    {u.type !== 'system' && (
                                        <button
                                            onClick={() => handleRemoveWithAnimation(u.id)}
                                            aria-label={t('catalog.delete_unit', 'Удалить единицу')}
                                            className="p-2 text-slate-300 dark:text-slate-600 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                        >
                                            <Trash2 size={16} aria-hidden="true" />
                                        </button>
                                    )}
                                </div>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};
